import { hex4, type DocNode } from "./document.js";

const EMPTY_STRING = '`""`';

/**
 * Escape the character at `i` for inline, one-line display: control characters
 * become visible JSON-style escapes, valid surrogate pairs pass through, lone
 * surrogates become visible, and Markdown/HTML metacharacters are neutralised.
 * Spaces and newlines are handled by callers (space-run and line logic), so a
 * literal space returns unchanged. Returns the escaped text and code units read.
 */
function escapeInlineChar(str: string, i: number): [string, number] {
  const code = str.charCodeAt(i);
  switch (code) {
    case 0x09:
      return ["\\t", 1];
    case 0x08:
      return ["\\b", 1];
    case 0x0c:
      return ["\\f", 1];
    case 0x0a:
      return ["\\n", 1];
    case 0x0d:
      return ["\\r", 1];
  }
  if (code < 0x20 || (code >= 0x7f && code <= 0x9f)) return ["\\u" + hex4(code), 1];

  if (code >= 0xd800 && code <= 0xdbff) {
    const next = i + 1 < str.length ? str.charCodeAt(i + 1) : 0;
    if (next >= 0xdc00 && next <= 0xdfff) return [str[i]! + str[i + 1]!, 2];
    return ["\\u" + hex4(code), 1];
  }
  if (code >= 0xdc00 && code <= 0xdfff) return ["\\u" + hex4(code), 1];

  switch (str[i]) {
    case "\\":
      return ["\\\\", 1];
    case "`":
      return ["\\`", 1];
    case "*":
      return ["\\*", 1];
    case "_":
      return ["\\_", 1];
    case "[":
      return ["\\[", 1];
    case "]":
      return ["\\]", 1];
    case "|":
      return ["\\|", 1];
    case "~":
      return ["\\~", 1];
    case "&":
      return ["&amp;", 1];
    case "<":
      return ["&lt;", 1];
    case ">":
      return ["&gt;", 1];
    default:
      return [str[i]!, 1];
  }
}

/**
 * Escape one line segment of a Literal String. Newlines are handled by the
 * caller; this sees text with no `\n`/`\r`.
 */
function renderSegment(seg: string): string {
  let out = "";
  const n = seg.length;
  let i = 0;
  let atStart = true;

  while (i < n) {
    const code = seg.charCodeAt(i);

    // Space runs: keep a single interior space, protect everything Markdown
    // would collapse (leading, trailing, additional repeats) with `&nbsp;`.
    if (code === 0x20) {
      let j = i;
      while (j < n && seg.charCodeAt(j) === 0x20) j++;
      const runLen = j - i;
      const interior = i > 0 && j < n;
      out += interior ? " " + "&nbsp;".repeat(runLen - 1) : "&nbsp;".repeat(runLen);
      i = j;
      atStart = false;
      continue;
    }

    // Block markers only bite at the very start of a segment.
    if (atStart) {
      if (code >= 0x30 && code <= 0x39) {
        let j = i;
        while (j < n) {
          const c = seg.charCodeAt(j);
          if (c >= 0x30 && c <= 0x39) j++;
          else break;
        }
        const punct = seg[j];
        if (j < n && (punct === "." || punct === ")")) {
          out += seg.slice(i, j) + "\\" + punct;
          i = j + 1;
          atStart = false;
          continue;
        }
      } else if (code === 0x23 /* # */ || code === 0x2d /* - */ || code === 0x2b /* + */) {
        out += "\\" + seg[i];
        i++;
        atStart = false;
        continue;
      }
    }

    const [text, consumed] = escapeInlineChar(seg, i);
    out += text;
    i += consumed;
    atStart = false;
  }

  return out;
}

/**
 * Escape a string for one-line inline display (a Key Label or a JSON Pointer):
 * the same space-run and metacharacter handling as a Literal String segment, but
 * newlines stay as `\n`/`\r` (via `escapeInlineChar`) so the text never leaves its
 * line, and no block-start markers are escaped. Converting boundary and collapsible
 * spaces to `&nbsp;` keeps headings free of trailing spaces and keeps a heading's
 * slug stable (a GFM renderer trims trailing spaces but preserves entities).
 */
export function escapeInline(str: string): string {
  let out = "";
  const n = str.length;
  let i = 0;
  while (i < n) {
    if (str.charCodeAt(i) === 0x20) {
      let j = i;
      while (j < n && str.charCodeAt(j) === 0x20) j++;
      const runLen = j - i;
      const interior = i > 0 && j < n;
      out += interior ? " " + "&nbsp;".repeat(runLen - 1) : "&nbsp;".repeat(runLen);
      i = j;
      continue;
    }
    const [text, consumed] = escapeInlineChar(str, i);
    out += text;
    i += consumed;
  }
  return out;
}

/** Render an untrusted string as visible Literal String text (newlines become `<br>`). */
export function escapeText(value: string): string {
  const normalized = value.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const segments = normalized.split("\n");
  let out = "";
  for (let s = 0; s < segments.length; s++) {
    if (s > 0) out += "<br>";
    out += renderSegment(segments[s]!);
  }
  return out;
}

/** A string is a URL String only if its whole, unmodified content is one absolute http(s) URL. */
export function isUrlString(value: string): boolean {
  if (/\s/.test(value)) return false;
  if (!/^https?:\/\//i.test(value)) return false;
  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
    return url.host.length > 0;
  } catch {
    return false;
  }
}

/** Escape a link destination so it survives a table cell. */
function escapeDestination(url: string): string {
  return url.replace(/[\\()|]/g, (c) => "\\" + c);
}

/**
 * The literal, escaped display of an object member name. Unlike a Literal String,
 * a Key Label keeps control characters as visible JSON-style escapes (`\n`, `\t`,
 * ` `) so the label stays on one line inside a heading or table header.
 */
export function keyLabel(name: string): string {
  if (name === "") return EMPTY_STRING;
  return escapeInline(name);
}

export type AnnotationType = "string" | "integer" | "number" | "boolean";

/** The Type Annotation token appended after a value or a Uniform Column header. */
export function annotationSuffix(type: AnnotationType): string {
  return ` *(${type})*`;
}

/**
 * The Annotation Type of a value, or null for Self-Describing Values (null,
 * `[]`, `{}`) which never carry a Type Annotation. A number is an integer when
 * its Numeric Lexeme has no fraction or exponent part (`1.00` and `1e3` are
 * numbers).
 */
export function annotationType(node: DocNode): AnnotationType | null {
  switch (node.kind) {
    case "string":
      return "string";
    case "boolean":
      return "boolean";
    case "number":
      return /[.eE]/.test(node.lexeme) ? "number" : "integer";
    default:
      return null;
  }
}

/**
 * The inline representation of a Scalar Value, or of an empty container shown
 * inline as `` `[]` `` / `` `{}` ``. Same rendering in every context; table
 * cells rely on the same escaping.
 */
export function scalarText(node: DocNode): string {
  switch (node.kind) {
    case "null":
      return "`null`";
    case "boolean":
      return node.value ? "true" : "false";
    case "number":
      return node.lexeme;
    case "string": {
      if (node.value === "") return EMPTY_STRING;
      if (isUrlString(node.value)) {
        return `[${escapeText(node.value)}](${escapeDestination(node.value)})`;
      }
      return escapeText(node.value);
    }
    case "array":
      return "`[]`";
    case "object":
      return "`{}`";
  }
}
